import { _ as _defineProperty } from '../../unsorted/defineProperty.js';
import { _ as _objectWithoutProperties } from '../../unsorted/objectWithoutProperties.js';
import { useMemo } from 'react';
import { m as merge } from '../../unsorted/index.js';
import { DEFAULT_AWS_IMAGE_RESIZER_URL, DEFAULT_IMAGE_SIZE, DEFAULT_LOADING_IMG_ATTR } from '../../constants.js';
import { useConvertSxToEmotionStyles } from '../../hooks/useConvertSxToEmotionStyles.js';
import { c as base64UrlEncodeImageUrl, i as isImageResizerProps } from '../../utils/imageHelpers2.js';
import { jsx } from '@emotion/react/jsx-runtime';
import '@emotion/react';
import { useDetectSvgImage } from '../../hooks/useDetectSvgImage.js';
import { useDetectBadImage } from '../../hooks/useDetectBadImage.js';
import { useTheme } from '../../hooks/useTheme.js';
import { cloneElementWithCssProp } from '../../utils/componentHelpers.js';

var ON_LIGHT_DEFAULT_IMAGE = "https://biome.immutable.com/hosted-assets/images/cloudImageDefaultImage--onLight.svg";
var ON_DARK_DEFAULT_IMAGE = "https://biome.immutable.com/hosted-assets/images/cloudImageDefaultImage--onDark.svg";
var generateBaseImageUrl = function generateBaseImageUrl(_ref) {
  var imageUrl = _ref.imageUrl,
    imageResizeServiceUrl = _ref.imageResizeServiceUrl;
  return imageUrl ? "".concat(imageResizeServiceUrl, "/url=").concat(base64UrlEncodeImageUrl(imageUrl)) : undefined;
};
var generateResizedImageUrl = function generateResizedImageUrl(_ref2) {
  var width = _ref2.width,
    height = _ref2.height,
    imageUrl = _ref2.imageUrl,
    imageResizeServiceUrl = _ref2.imageResizeServiceUrl,
    isSvg = _ref2.isSvg;
  return imageUrl ? "".concat(generateBaseImageUrl({
    imageResizeServiceUrl: imageResizeServiceUrl,
    imageUrl: imageUrl
  })).concat(width && !isSvg ? "&w=".concat(width) : height && !isSvg ? "&h=".concat(height) : "") : undefined;
};

var _excluded$1 = ["css", "testId", "loading", "responsiveSizes", "relativeImageSizeInLayout", "domRef", "alt", "className", "imageUrl", "defaultImageClassName", "defaultImageUrl", "imageResizeServiceUrl"];
function ownKeys$1(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$1(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$1(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$1(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function AwsResizerImage(_ref) {
  var _ref2, _theme$globalConfig;
  var css = _ref.css,
    _ref$testId = _ref.testId,
    testId = _ref$testId === void 0 ? "AwsResizerImage" : _ref$testId,
    _ref$loading = _ref.loading,
    loading = _ref$loading === void 0 ? DEFAULT_LOADING_IMG_ATTR : _ref$loading,
    _ref$responsiveSizes = _ref.responsiveSizes,
    responsiveSizes = _ref$responsiveSizes === void 0 ? [32, 48, 64, 128, 256] : _ref$responsiveSizes,
    relativeImageSizeInLayout = _ref.relativeImageSizeInLayout,
    domRef = _ref.domRef,
    alt = _ref.alt,
    className = _ref.className,
    imageUrl = _ref.imageUrl,
    defaultImageClassName = _ref.defaultImageClassName,
    defaultImageUrl = _ref.defaultImageUrl,
    imageResizeServiceUrlProp = _ref.imageResizeServiceUrl,
    props = _objectWithoutProperties(_ref, _excluded$1);
  var theme = useTheme();
  var imageResizeServiceUrl = (_ref2 = imageResizeServiceUrlProp !== null && imageResizeServiceUrlProp !== void 0 ? imageResizeServiceUrlProp : (_theme$globalConfig = theme.globalConfig) === null || _theme$globalConfig === void 0 ? void 0 : _theme$globalConfig.imageResizeServiceUrl) !== null && _ref2 !== void 0 ? _ref2 : DEFAULT_AWS_IMAGE_RESIZER_URL;
  var pickedDefaultImage = useMemo(function () {
    return defaultImageUrl || (theme.base.colorMode === "onDark" ? ON_DARK_DEFAULT_IMAGE : ON_LIGHT_DEFAULT_IMAGE);
  }, [theme, defaultImageUrl]);
  var imageIsSvg = useDetectSvgImage({
    imageUrl: generateBaseImageUrl({
      imageUrl: imageUrl,
      imageResizeServiceUrl: imageResizeServiceUrl
    })
  });
  var imageIsValid = useDetectBadImage({
    imageUrl: generateResizedImageUrl({
      imageUrl: imageUrl,
      imageResizeServiceUrl: imageResizeServiceUrl,
      // @NOTE: to see if an exists, only try to get a tiny version of it (dont get the fullsized image)
      width: 32
    })
  });
  var srcSet = useMemo(function () {
    return responsiveSizes === null || responsiveSizes === void 0 ? void 0 : responsiveSizes.reduce(function (accum, current) {
      return "".concat(accum).concat(generateResizedImageUrl({
        imageUrl: imageUrl,
        imageResizeServiceUrl: imageResizeServiceUrl,
        width: current,
        isSvg: false
      }), " ").concat(current, "w, ");
    }, "");
  }, [responsiveSizes, imageUrl, imageResizeServiceUrl]);
  var sizesAttrToUse = useMemo(function () {
    if (!relativeImageSizeInLayout) return "";
    if (typeof relativeImageSizeInLayout === "function") {
      return relativeImageSizeInLayout(theme);
    }
    return relativeImageSizeInLayout;
  }, [relativeImageSizeInLayout, theme]);
  var srcAttrs = useMemo(function () {
    return imageIsSvg ? {
      src: generateResizedImageUrl({
        imageUrl: imageUrl,
        imageResizeServiceUrl: imageResizeServiceUrl,
        isSvg: true
      })
    } : srcSet ? {
      srcSet: srcSet,
      sizes: sizesAttrToUse
    } : {
      src: generateResizedImageUrl({
        imageUrl: imageUrl,
        imageResizeServiceUrl: imageResizeServiceUrl,
        width: DEFAULT_IMAGE_SIZE,
        isSvg: false
      })
    };
  }, [imageIsSvg, imageUrl, imageResizeServiceUrl, srcSet, sizesAttrToUse]);
  return imageIsValid ? jsx("img", _objectSpread$1(_objectSpread$1(_objectSpread$1({}, props), srcAttrs), {}, {
    alt: alt,
    className: "".concat(className !== null && className !== void 0 ? className : "", " AwsResizerImage"),
    css: css,
    "data-testid": testId,
    loading: loading,
    ref: domRef
  })) : jsx("img", _objectSpread$1(_objectSpread$1({}, props), {}, {
    ref: domRef,
    alt: alt,
    className: "".concat(className !== null && className !== void 0 ? className : "", " ").concat(defaultImageClassName !== null && defaultImageClassName !== void 0 ? defaultImageClassName : "", " AwsResizerImage AwsResizerImage--defaultImage"),
    css: css,
    "data-testid": "".concat(testId, "--defaultImage"),
    loading: loading,
    src: pickedDefaultImage
  }));
}

var _excluded = ["testId", "className", "domRef", "loading", "sx", "use"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function CloudImage(_ref) {
  var _ref$testId = _ref.testId,
    testId = _ref$testId === void 0 ? "CloudImage" : _ref$testId,
    className = _ref.className,
    domRef = _ref.domRef,
    _ref$loading = _ref.loading,
    loading = _ref$loading === void 0 ? DEFAULT_LOADING_IMG_ATTR : _ref$loading,
    _ref$sx = _ref.sx,
    sx = _ref$sx === void 0 ? {} : _ref$sx,
    use = _ref.use,
    props = _objectWithoutProperties(_ref, _excluded);
  var mergedSx = useMemo(function () {
    return merge({
      minw: "base.icon.size.200"
    }, sx);
  }, [sx]);
  var customStyles = useConvertSxToEmotionStyles(mergedSx);
  var mergedClassName = "".concat(className !== null && className !== void 0 ? className : "", " CloudImage");
  var shouldUseImageResizer = isImageResizerProps(use);
  return shouldUseImageResizer ? jsx(AwsResizerImage, _objectSpread(_objectSpread({}, props), {}, {
    className: mergedClassName,
    domRef: domRef,
    css: customStyles,
    loading: loading,
    testId: testId
  })) : cloneElementWithCssProp(use, _objectSpread(_objectSpread({}, props), {}, {
    loading: loading,
    ref: domRef,
    css: customStyles,
    className: mergedClassName,
    "data-testid": testId
  }));
}
CloudImage.displayName = "CloudImage";

export { CloudImage as C, ON_LIGHT_DEFAULT_IMAGE as O, ON_DARK_DEFAULT_IMAGE as a };
//# sourceMappingURL=CloudImage.js.map
